

import { lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { ACCOUNT_TEARDOWN_EVENT } from "@/lib/storage/accountScopedState";
import { useTranslations } from "next-intl";
import {
  describeJobError,
  translateBackendError,
} from "@/lib/i18n/translateBackendError";
import { motion, AnimatePresence } from "framer-motion";
import {
  IconAlertTriangle,
  IconCheck,
  IconDownload,
  IconLoader2,
  IconX,
  IconChevronUp,
  IconChevronDown,
  IconPlayerStop,
} from "@tabler/icons-react";

import {
  useBackgroundJobs,
  TrackedJob,
  TrackedJobTaskType,
  type CancelJobOutcome,
  isJobGone,
  scopeKindFor,
} from "@/providers/BackgroundJobsProvider";
import { useActionError } from "@/lib/errors/useActionError";
import { useToast } from "@/components/Toast/Toast";
import { ImportRowError, type ArrivalNoticeWarning } from "@/types/BackgroundJob";
import { formatFileSize } from "@/lib/utils/email-attachment-validation";
import { useState, useEffect, useCallback, useMemo, useId } from "react";
import { Link, useParams, useLocation, useNavigate as useRouter } from "react-router-dom";
import { smsHaltReasonKey } from "@/lib/utils/smsHaltReason";
import { smsSendViewPath } from "@/lib/sms/smsSentPaths";
import { isSmsManualDeadFanout, SMS_MANUAL_NOTHING_SENT_CODES, smsManualEndWordsCode } from "@/lib/sms/smsManualSend";
import { useAuth } from "@/lib/auth/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getCompanyRole } from "@/lib/api/handlers/CompanyRoleHandling";
import { useCanBuyEventLicence } from "@/hooks/useCanBuyEventLicence";
import { useEventLayoutRoleQuery } from "@/lib/query/hooks/useEventLayoutRole";
import { useCoachStore } from "@/lib/onboarding/coach-store";
import { isCreateEventRoute } from "@/lib/immersiveRoutes";

const PDeleteConfirmation = lazy(() => import("@/components/PDeleteConfirmation/PDeleteConfirmation").then(m => ({ default: m.PDeleteConfirmation })));

type TaskTypeCategory = "email" | "sms" | "export" | "import" | "bulkUpdate" | "batchCheckIn" | "translate" | "analyzeGoals";

/**
 * Row presentation bucket for a task type.
 *
 * ⚠ THE LAST BRANCH IS A FALLBACK, not a rule: anything unmatched renders as
 * "import", label and all — including `backgroundJobs.import.completedDescription`,
 * "All {count} entries were imported successfully". A task type that is not an
 * import inherits that sentence.
 *
 * Measured 2026-08-15: nothing currently reaches that branch by accident. The
 * only task types a surface actually tracks are the ones asserted in
 * `PBackgroundJobs.summary.test.tsx` (describe "PBackgroundJobs task-type
 * buckets"), and each maps explicitly above. `SCHEDULED_WORKFLOW` sits in the union with a
 * note saying it is "tracked here only if a surface chooses to poll it" — none
 * does, so it never renders. That is why this is a latent defect and not a live
 * one, and why fixing it with a new category (and 57 translations) would be
 * building a room nobody enters.
 *
 * The pin in the test is the part that matters: add a tracked task type without
 * a bucket and it goes red, rather than quietly claiming guests were imported.
 */
function getTaskCategory(taskType: TrackedJobTaskType): TaskTypeCategory {
  if (taskType === "SEND_EMAILS") return "email";
  if (taskType === "SEND_SMS_MANUAL") return "sms";
  if (taskType.startsWith("EXPORT")) return "export";
  if (taskType.startsWith("BULK_UPDATE_GUESTS")) return "bulkUpdate";
  if (taskType === "BATCH_CHECK_IN_GUESTS") return "batchCheckIn";
  if (taskType === "TRANSLATE_TEMPLATE") return "translate";
  if (taskType === "ANALYZE_EVENT_GOALS") return "analyzeGoals";
  return "import";
}

// Number of `tipN` keys per category in the backgroundJobs.{category}.tips
// i18n object. next-intl doesn't natively unpack arrays, so the hook iterates
// tip1..tipN explicitly — same pattern as PSavePage. Keep these counts in sync
// with en-US.json's backgroundJobs.{category}.tips.
const TIP_COUNTS: Record<TaskTypeCategory, number> = {
  email: 8,
  sms: 5,
  export: 8,
  import: 6,
  bulkUpdate: 5,
  batchCheckIn: 5,
  translate: 5,
  analyzeGoals: 4,
};
const TIP_INTERVAL_MS = 3000;

// Task types whose backend job reports a real per-item counter: `total` is set
// up-front and `processedCount` climbs as items land — queue-backed
// (SEND_EMAILS → bulk-email.ts per recipient; IMPORT_GUESTS_* /
// CONTACT_IMPORT_CREATE → csv-import.ts / contact-import.ts per chunk) and,
// since 2026-08-26, the Java executor lanes too (bulk update, batch check-in,
// and the in-process import lanes via HeavyProcessingService's
// JobCounterReporter). For these we render "x of y …" from the live counters.
// Translate stays out: it has no meaningful item count, so it keeps the percent
// bar + rotating tips.
const COUNTED_TASK_TYPES: ReadonlySet<TrackedJobTaskType> =
  new Set<TrackedJobTaskType>([
    "SEND_EMAILS",
    // SMS manual send (F-3): the consumer sets `total` before its first
    // enqueue and counts every guest once — sent, skipped, failed or dry-run
    // alike, hence "processed", not "sent" (F3-12).
    "SEND_SMS_MANUAL",
    "IMPORT_GUESTS_CREATE",
    "IMPORT_GUESTS_OVERWRITE",
    "CONTACT_IMPORT_CREATE",
    "BULK_UPDATE_GUESTS_BY_FILTER",
    "BULK_UPDATE_GUESTS_BY_IDS",
    "BATCH_CHECK_IN_GUESTS",
    // The XLSX exports count rows since 2026-08-26, but only on the EXECUTOR
    // lane: its builders buffer the rows before writing them, so the
    // denominator costs nothing. The CSV exports stream straight into the R2
    // body and have no count at any point, which is why they stay out and keep
    // the bar + tips.
    //
    // The queue lane — which is the DEFAULT for all of these — writes no
    // counters, and the serverTotal gate below is what covers that. ⚠ That
    // sentence was already here when it was false: the producer used to stamp
    // `total = 1` as a completion sentinel, which satisfies `serverTotal !=
    // null`, so a 12 000-row export read "0 of 1 rows exported" for its whole
    // run. The sentinel is gone (nothing read it — export-job.ts completes
    // through the unconditional markJobCompleted). Re-adding any queue-side
    // `total` that is not a row count re-opens this.
    "EXPORT_GUESTS_TO_EXCEL",
    "EXPORT_SPECIFIC_GUESTS_TO_EXCEL",
    "EXPORT_EVENTS_TO_EXCEL",
    "EXPORT_SPECIFIC_EVENTS_TO_EXCEL",
    "EXPORT_EMAILS_SENT_TO_EXCEL",
    "EXPORT_SPECIFIC_EMAILS_SENT_TO_EXCEL",
    "EXPORT_REQUESTS_TO_EXCEL",
    "EXPORT_CHECK_IN_LOGS_TO_EXCEL",
  ]);

// …of those, the ones that may ONLY show the line once the SERVER has confirmed
// a total (`serverTotal != null`), never from the client's submit-time estimate.
//
// This is a deploy-order guard, not a display preference. The counters for
// these task types are new on the backend; the frontend has always sent a
// sensible `totalItems` at submit (PBatchCheckIn's matchingCount, PBulkEdit's
// live count), so shipping the frontend first — or hitting an instance that has
// not rolled yet — would render a "0 of N" that never moves. That frozen line is
// the exact defect this whole change removes.
//
// SEND_EMAILS and the guest-import types are deliberately absent: their counters
// have shipped for a long time, and there the submit-time estimate is wanted (it
// fills the moment before the first poll).
const SERVER_CONFIRMED_COUNT_TYPES: ReadonlySet<TrackedJobTaskType> =
  new Set<TrackedJobTaskType>([
    "CONTACT_IMPORT_CREATE",
    "BULK_UPDATE_GUESTS_BY_FILTER",
    "BULK_UPDATE_GUESTS_BY_IDS",
    "BATCH_CHECK_IN_GUESTS",
    // The eight buffered XLSX exports: their counters are new, AND only the
    // executor lane writes them — an export that ran on the queue has no total
    // at all. The gate is what keeps that row on the bar + tips instead of a
    // frozen zero, so for these it is not a deploy-order guard but the
    // permanent lane discriminator (the poll payload carries no executionLane).
    //
    // EXPORT_SLOTTING_REQUESTS_TO_EXCEL is the one *_TO_EXCEL type missing
    // here, and on purpose: QueueConfig.isQueueOnlyTask lists it, so it has no
    // executor lane and can never produce a row count. It is absent from
    // COUNTED_TASK_TYPES for the same reason.
    "EXPORT_GUESTS_TO_EXCEL",
    "EXPORT_SPECIFIC_GUESTS_TO_EXCEL",
    "EXPORT_EVENTS_TO_EXCEL",
    "EXPORT_SPECIFIC_EVENTS_TO_EXCEL",
    "EXPORT_EMAILS_SENT_TO_EXCEL",
    "EXPORT_SPECIFIC_EMAILS_SENT_TO_EXCEL",
    "EXPORT_REQUESTS_TO_EXCEL",
    "EXPORT_CHECK_IN_LOGS_TO_EXCEL",
  ]);

// `getTaskCategory` buckets the CRM contact import into `import`, whose
// countProgress says "guests". Contacts are not guests, so this one task type
// reads its own key. Everything else follows its category.
const COUNT_PROGRESS_KEY_OVERRIDES: Partial<Record<TrackedJobTaskType, string>> = {
  CONTACT_IMPORT_CREATE: "contactImport.countProgress",
};

type BatchCheckInResult = {
  checkIn?: boolean;
  /** Reset-to-never-scanned run. Carries NO `checkIn` key — the direction
   *  ternary below would otherwise label the repair as one of the two
   *  directions it undoes. */
  reset?: boolean;
  processed: number;
  blocked?: boolean;
  currentCount?: number;
  capacity?: number;
  warningsCount?: number;
};

/**
 * Parses a completed BATCH_CHECK_IN_GUESTS job's `result` JSON. Defensive by
 * construction: old-shape results (processed+blocked only, pre capacity
 * context) parse fine and degrade to the processed line; export URLs,
 * absent results, and garbage return null and render nothing.
 */
/**
 * PB-TG-5 — a completed SEND_EMAILS job whose `result` reports attachment
 * degradation.
 *
 * A template whose attachment cannot be resolved still SENDS (deliberately: a
 * missing venue map must not black-hole a confirmation), and that used to be
 * recorded ONLY as `attachmentsDegraded` on each per-recipient sent_emails
 * row. Nothing aggregated it, so a campaign that shipped N attachment-less
 * emails reported a clean success and the organiser never found out.
 *
 * Same defensive shape as the batch-check-in parser below: the field is also
 * where an EXPORT job stores its download URL, so anything that is not this
 * exact envelope must return null rather than render.
 */
function parseDegradedSendResult(raw: string | undefined): number | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.taskType !== "SEND_EMAILS") return null;
    const n = parsed.attachmentsDegraded;
    return typeof n === "number" && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * An import that committed its guests but lost one or more automation
 * announcements. Written by the QUEUE lane (`main-consumer`
 * `background-job.ts`, folded into `result` at completion) — which is where
 * imports actually run, `queue.task.CSV_IMPORT.enabled` defaulting on.
 *
 * ⚠ Gated on `taskType`, never on the row's CATEGORY. `getTaskCategory` treats
 * "import" as its FALLBACK bucket, so any unmatched task type — `SCHEDULED_WORKFLOW`
 * is the one in the union today — would land in that category and a
 * category-gated parser would read its result. (Measured: no surface tracks
 * such a job, so nothing reaches it in practice. The gate is here so that stays
 * true by construction rather than by luck.) Listing the task types explicitly
 * is what the two parsers above do, for the same reason.
 *
 * Both guest-import types are accepted; `CONTACT_IMPORT_CREATE` is a
 * company-scoped CRM import on a different lane that never writes this field,
 * and is left out until it does.
 */
const IMPORT_TASK_TYPES_WITH_RESULT = new Set([
  "IMPORT_GUESTS_CREATE",
  "IMPORT_GUESTS_OVERWRITE",
]);

function parseImportAnnouncementsLost(raw: string | undefined): number | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!IMPORT_TASK_TYPES_WITH_RESULT.has(parsed?.taskType)) return null;
    const n = parsed.announcementsLost;
    return typeof n === "number" && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * W4 Hop B (contract-b §7 / §10.3): what the import's arrival-notice linker
 * did — links set, links not set, and why per row (no address, ever). Read
 * from the job's own fields (the lazy terminal fetch carries them) and, for a
 * producer that folds them into `result` instead, from the result JSON. Gated
 * on the guest-import task types like the parser above.
 */
type ArrivalNoticeOutcome = { linked: number; skipped: number; warnings: ArrivalNoticeWarning[] };

const countOrZero = (n: unknown): number => (typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0);

export function arrivalNoticeOutcomeOf(job: {
  taskType: string;
  result?: string;
  arrivalNoticeLinked?: number;
  arrivalNoticeSkipped?: number;
  arrivalNoticeWarnings?: ArrivalNoticeWarning[];
}): ArrivalNoticeOutcome | null {
  if (!IMPORT_TASK_TYPES_WITH_RESULT.has(job.taskType)) return null;
  let source: Record<string, unknown> = job as unknown as Record<string, unknown>;
  if (job.arrivalNoticeLinked == null && job.arrivalNoticeSkipped == null && job.result) {
    try {
      const parsed = JSON.parse(job.result);
      if (parsed && typeof parsed === "object") source = parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  const linked = countOrZero(source.arrivalNoticeLinked);
  const skipped = countOrZero(source.arrivalNoticeSkipped);
  const warnings = Array.isArray(source.arrivalNoticeWarnings)
    ? (source.arrivalNoticeWarnings as unknown[]).filter(
        (w): w is ArrivalNoticeWarning => !!w && typeof w === "object",
      )
    : [];
  if (linked === 0 && skipped === 0 && warnings.length === 0) return null;
  return { linked, skipped, warnings };
}

/** The linker's reasons (contract-b §7); anything else reads generically. */
const ARRIVAL_NOTICE_REASONS = new Set([
  "contactRemoved",
  "contactNotLinked",
  "guestLinksCap",
  "contactsPerGuestCap",
  "rowRefused",
  "guestNotConfirmed",
]);

/** Warnings listed per row before "and N more". */
const ARRIVAL_NOTICE_WARNINGS_SHOWN = 10;

function parseBatchCheckInResult(raw: string | undefined): BatchCheckInResult | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed?.taskType !== "BATCH_CHECK_IN_GUESTS" ||
      typeof parsed.processed !== "number"
    ) {
      return null;
    }
    return parsed as BatchCheckInResult;
  } catch {
    return null;
  }
}

/**
 * What a by-ids bulk edit left undone, from the counts main-backend adds to
 * its result (plan graceful-giggling-melody §BE 5, from T2 on — additive:
 * `succeeded` / `failed` and the job status keep their meaning, and this
 * NEVER reads `failed`, which also counts unchanged rows).
 *
 *  - `failedByCode`: rows the server refused, by error code;
 *  - `unchanged`: rows that already had the value (not a failure);
 *  - `notInEvent`: selected ids that are no longer in the event or outside
 *    the role's rows — the field pass then writes for nobody (⚑2);
 *  - `notApplied`: how many rows did not get the change (a notInEvent run is
 *    all-or-nothing); the row shows the larger of it and the refused rows.
 *
 * `null` for a result without any of them — every job before T2 — so the row
 * renders exactly as it did.
 */
type BulkUpdateResult = {
  failedByCode: Record<string, number>;
  unchanged: number | null;
  notInEvent: number;
  notApplied: number | null;
};

function parseBulkUpdateResult(raw: string | undefined): BulkUpdateResult | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || parsed.taskType !== "BULK_UPDATE_GUESTS_BY_IDS") return null;
    const count = (v: unknown): number | null =>
      typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
    const byCodeRaw = parsed.failedByCode;
    const hasByCode = !!byCodeRaw && typeof byCodeRaw === "object" && !Array.isArray(byCodeRaw);
    if (
      !hasByCode &&
      count(parsed.unchanged) === null &&
      count(parsed.notInEvent) === null &&
      count(parsed.notApplied) === null
    ) {
      return null;
    }
    const failedByCode: Record<string, number> = {};
    if (hasByCode) {
      for (const [code, n] of Object.entries(byCodeRaw as Record<string, unknown>)) {
        const c = count(n);
        if (c !== null && c > 0) failedByCode[code] = c;
      }
    }
    return {
      failedByCode,
      unchanged: count(parsed.unchanged),
      notInEvent: count(parsed.notInEvent) ?? 0,
      notApplied: count(parsed.notApplied),
    };
  } catch {
    return null;
  }
}

/** At most this many distinct reasons under the "not updated" line. */
const MAX_BULK_UPDATE_REASONS = 3;

function useJobTipsRotator(category: TaskTypeCategory, isActive: boolean): string {
  const t = useTranslations(`backgroundJobs.${category}.tips`);
  const tips = useMemo(() => {
    const count = TIP_COUNTS[category];
    const arr: string[] = [];
    for (let i = 1; i <= count; i++) arr.push(t(`tip${i}`));
    return arr;
  }, [category, t]);
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * Math.max(1, TIP_COUNTS[category])),
  );
  useEffect(() => {
    if (!isActive || tips.length === 0) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % tips.length);
    }, TIP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isActive, tips.length]);
  return tips[index % tips.length] ?? "";
}

const StatusIcon: React.FC<{
  status: TrackedJob["status"];
  size?: number;
  /**
   * PB-TL-3: COMPLETED but with failed items. The job DID finish, so this is
   * not the error icon — but it must not be the plain green check either,
   * which is what previously rendered over a campaign that dropped half its
   * recipients.
   */
  partial?: boolean;
}> = ({ status, size = 14, partial = false }) => {
  if (status === "COMPLETED" && partial)
    return (
      // The warning banner's own pair (both themes): `surface-warning` /
      // `font-warning` are declared nowhere, so the triangle rendered black
      // on no circle (screens r2 09).
      <div
        className="w-6 h-6 rounded-full surface-status-amber-bg flex items-center justify-center shrink-0"
        data-testid="job-status-partial"
      >
        <IconAlertTriangle size={size} className="font-status-amber-dark" />
      </div>
    );
  if (status === "COMPLETED")
    return (
      <div className="w-6 h-6 rounded-full surface-success flex items-center justify-center shrink-0">
        <IconCheck size={size} className="font-success" />
      </div>
    );
  if (status === "FAILED")
    return (
      <div className="w-6 h-6 rounded-full surface-error flex items-center justify-center shrink-0">
        <IconX size={size} className="font-error" />
      </div>
    );
  if (status === "CANCELLED")
    return (
      <div className="w-6 h-6 rounded-full surface-quaternary flex items-center justify-center shrink-0">
        <IconPlayerStop size={size} className="font-tertiary" />
      </div>
    );
  return (
    <div className="w-6 h-6 rounded-full surface-blue flex items-center justify-center shrink-0">
      <IconLoader2 size={size} className="font-blue animate-spin" />
    </div>
  );
};

/** Roles allowed to mutate an existing background job, in either scope. */
const JOB_MUTATING_ROLES = new Set(["OWNER", "ADMIN", "EDITOR"]);

/**
 * PB-TL-6 — may this user cancel / dismiss THIS job?
 *
 * The panel used to render the ✕ for everyone. On COMPANY-scope jobs (the
 * events exports) that was a real hole: the only server-side check was
 * @RolesAllowed against the company role, and it listed VIEWER, so a read-only
 * collaborator could abort or delete a colleague's export.
 *
 * The gate is per SCOPE, because the server's is:
 *
 *  - company-scope → the caller's COMPANY role decides, and nothing else does.
 *  - event-scope   → the caller's EVENT role decides. isValidEventToken matches
 *    the event role against the endpoint's list AFTER parseEventRoles, which
 *    maps only EVENT-* names, so the effective allowlist is
 *    {OWNER, ADMIN, EDITOR} and a company role of OWNER buys nothing without an
 *    event role (no event_role at all → 401). Gating on "company OR event"
 *    would therefore show the control to a company ADMIN whose event role is
 *    VIEWER — a button that 403s, the exact thing this gate exists to prevent.
 *
 * Both reads reuse the query keys the layout shells already populate, so on any
 * normal route this is a cache hit and costs no request. Fail-closed while
 * unresolved: a control that appears late beats one that appears for someone
 * who cannot use it.
 *
 * Also the per-send view's rule for its own cancel of a manual SMS send that
 * never started (audit r3 R3-1) — the same right as the tray's ✕.
 */
export function useJobControls(
  job: Pick<TrackedJob, "eventId" | "taskType" | "scopeKind">,
): { canMutate: boolean; eventGone: boolean } {
  const { state: authState } = useAuth();
  const authed = authState.kind === "authenticated";
  // Same fallback as the provider's routing: rows stored before `scopeKind`
  // existed carry only the task type (integration audit r4, R4-2).
  const isCompanyScoped = (job.scopeKind ?? scopeKindFor(job.taskType)) === "company";

  const companyRoleQ = useQuery({
    queryKey: ["userLayout", "companyRole"],
    queryFn: () => getCompanyRole("role"),
    enabled: authed && isCompanyScoped,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Company-scope jobs store the companyId in job.eventId (scopeId
  // convention) — there is no event role to read for them.
  const eventId = isCompanyScoped ? "" : job.eventId;
  // Same key AND same field list as EventLayoutShell — one shared hook now, so
  // the two can't race two differently-shaped payloads into one cache entry.
  const eventRoleQ = useEventLayoutRoleQuery(eventId, { enabled: authed });

  const roleOf = (data: unknown): string | null => {
    if (!data || typeof data !== "object") return null;
    if ("isServerError" in data) return null;
    const r = data as { role?: string; legacyRoleFloor?: string };
    // CUSTOM collapses to the server-computed floor; anything else is the role
    // itself. Matches useCapabilities().legacyRoleFloor. EventRoleDTO.role
    // carries the PLAIN form — "EVENT-" is a server-side annotation convention
    // and never appears in the payload.
    return (r.role === "CUSTOM" ? r.legacyRoleFloor : r.role) ?? null;
  };

  const role = roleOf(isCompanyScoped ? companyRoleQ.data : eventRoleQ.data);
  return {
    canMutate: role !== null && JOB_MUTATING_ROLES.has(role),
    // The event was deleted: its role read answers 404 notFound like the
    // job's own routes. A finished row then had no ✕ at all and stayed until
    // logout (integration audit r3, R3-1).
    eventGone: !isCompanyScoped && isJobGone(eventRoleQ.data),
  };
}

/** Keep in step with `BackgroundJobResource.OWNER_ADMIN_RESULT_TASK_TYPES`. */
const OWNER_ADMIN_RESULT_TASK_TYPES: ReadonlySet<string> = new Set([
  "EXPORT_EVENT_CONFIG_TO_JSON",
  "EXPORT_ATTACHMENTS_TO_ZIP",
]);

export const JobRow: React.FC<{
  job: TrackedJob;
  onDismiss: (opts?: { local?: boolean }) => void;
  onCancel: () => Promise<CancelJobOutcome>;
  tick: number;
}> = ({ job, onDismiss, onCancel, tick }) => {
  const t = useTranslations("backgroundJobs");
  const tTimes = useTranslations("times");
  const tErrors = useTranslations("errors");
  // The refused row names its column by field key (`mainGuest`); the operator
  // knows it as "Hauptgast" — the same label the mapper showed them.
  const tFields = useTranslations("guest-fields");
  // Reuses the import dialog's own column words (Row / Target field / …), so
  // the job panel names a rejected row exactly as the downloadable error list
  // does — and adds no new label to 57 bundles.
  const tImport = useTranslations("importPopup");
  const tHalt = useTranslations("smsHaltReason");
  const router = useRouter();
  const pathname = useLocation().pathname;
  const params = useParams();
  const category = getTaskCategory(job.taskType);
  const percentage = Math.round(job.progress);
  const { canMutate, eventGone } = useJobControls(job);

  const relativeTime = useMemo(() => {
    if (!job.createdAt) return "";
    const diffMs = Date.now() - job.createdAt;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return tTimes("now");
    if (diffMinutes === 1) return `${diffMinutes} ${tTimes("minute")}`;
    if (diffMinutes < 60) return `${diffMinutes} ${tTimes("minutes")}`;
    if (diffHours === 1) return `${diffHours} ${tTimes("hour")}`;
    if (diffHours < 24) return `${diffHours} ${tTimes("hours")}`;
    if (diffDays === 1) return `${diffDays} ${tTimes("day")}`;
    if (diffDays < 365) return `${diffDays} ${tTimes("days")}`;
    if (diffYears === 1) return `${diffYears} ${tTimes("year")}`;
    return `${diffYears} ${tTimes("years")}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.createdAt, tTimes, tick]);

  const handleEventClick = useCallback(() => {
    // Company-scope jobs (events export) store companyId in job.eventId — the
    // /event/{id}/... URL would be broken. Land on the dashboard root, which
    // is where the export was triggered from.
    if (job.scopeKind === "company") {
      if (pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.dispatchEvent(new Event("navigationStart"));
        router("/");
      }
      return;
    }
    const targetPath = `/event/${job.eventId}/guests/guestlist`;
    const currentEventId = params.eventId as string | undefined;
    if (currentEventId === job.eventId && pathname?.includes("/guests/guestlist")) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.dispatchEvent(new Event("navigationStart"));
      router(targetPath);
    }
  }, [job.eventId, job.scopeKind, params.eventId, pathname, router]);
  const isInProgress = job.status === "IN_PROGRESS";
  const isCompleted = job.status === "COMPLETED";
  const isFailed = job.status === "FAILED";
  const isCancelled = job.status === "CANCELLED";
  const isFinished = !isInProgress;
  // Read the plan only for a send refused for the quota — and only the plan
  // of the company the send was booked against: the event's owner. A
  // company-scope job carries the login company in `job.eventId`, not an event.
  const canBuyEventLicence = useCanBuyEventLicence(
    isFailed && /^emailQuotaExceeded(?::|$)/.test(job.errorMessage ?? ""),
    job.scopeKind === "company" ? null : job.eventId,
  );
  // Live "x of y …". Prefer the server total (items the backend actually
  // resolved — post opt-in/quota/dedup for a send, the ELIGIBLE rows for a batch
  // check-in) over the FE's submit-time estimate; fall back to it until the
  // first poll carries the server count. Clamp processed ≤ total so a brief
  // total-lags-processed window can't read "501 of 500".
  const countTotal = job.serverTotal ?? job.totalItems ?? 0;
  const countProcessed = Math.min(job.processedItems ?? 0, countTotal || 0);
  const showCount =
    isInProgress &&
    COUNTED_TASK_TYPES.has(job.taskType) &&
    countTotal > 0 &&
    // See SERVER_CONFIRMED_COUNT_TYPES: for the lanes whose counters are new,
    // an estimate alone is not enough to start promising a number.
    (!SERVER_CONFIRMED_COUNT_TYPES.has(job.taskType) || job.serverTotal != null);
  // Rotating taglines (PSavePage-style) so the row feels alive between progress
  // bumps. For a 180k-recipient bulk send the bar may only tick every ~30 s at
  // realistic SES throughput; the tagline rotation keeps the user oriented.
  //
  // Suppressed once the count is actually MOVING — not merely present. The
  // difference is not academic: the bulk-update lane reports per 500-row chunk,
  // so a bulk edit of 300 guests emits exactly two ticks, "0 of 300" and
  // "300 of 300". Gating on `showCount` alone replaced a rotating tagline with
  // a frozen zero for the whole run — the same "nothing is happening" reading
  // the counters were introduced to remove, just wearing a number.
  const showTips =
    isInProgress && percentage < 99 && !(showCount && countProcessed > 0);
  const currentTip = useJobTipsRotator(category, showTips);
  const [errorExpanded, setErrorExpanded] = useState(false);
  const errorDetailId = useId();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // PB-TL-5 clock. A plain Date.now() at render would freeze: the panel is
  // often left open, and the row would keep offering a link long past expiry
  // because nothing re-rendered it. Ticks once a minute, and ONLY while this
  // row is a completed export that has not expired yet — so it costs nothing
  // on every other row and stops itself the moment it has done its job.
  // The links are signed at COMPLETION. `completedAt` arrives with the lazy
  // terminal fetch; without it (an older tracked job) the submit time is the
  // only clock there is — early, never late.
  const signedAt = job.completedAt ?? job.createdAt;
  const isExportResult =
    job.status === "COMPLETED" &&
    category === "export" &&
    Boolean(job.result) &&
    typeof signedAt === "number";
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!isExportResult) return;
    if (Date.now() - (signedAt ?? 0) > 3600_000) return;
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [isExportResult, signedAt]);

  // PB-TL-5: an export's `result` is an S3 PRESIGNED url with a 1h TTL
  // (export-job.ts PRESIGN_TTL_SECONDS = 3600). The row used to render the
  // Download link forever, so a job left in the panel overnight — or restored
  // from localStorage on the next visit, which is the common case — offered a
  // link that 403s with no explanation. Age it out on the client instead:
  // once past the TTL the link is replaced by an "expired" note.
  //
  // Deliberately generous by a minute: `createdAt` is the SUBMIT time while
  // the URL is presigned at COMPLETION, so the true expiry is always a little
  // later than this computes. Erring on the side of hiding a link that might
  // still work beats showing one that certainly does not — the user can
  // re-run the export, but cannot debug a 403.
  const EXPORT_URL_TTL_MS = 3600_000;
  const isDownloadExpired =
    isCompleted &&
    category === "export" &&
    typeof signedAt === "number" &&
    nowMs - signedAt > EXPORT_URL_TTL_MS;
  const hasDownload =
    isCompleted && category === "export" && job.result && !isDownloadExpired;
  // A multi-file result (the volumes of an attachment ZIP) replaces the single
  // link: `result` only repeats the first volume for clients that know nothing else.
  const resultFiles = hasDownload && job.resultFiles && job.resultFiles.length > 0 ? job.resultFiles : null;
  // The backend withholds the download of these task types from everyone but
  // OWNER/ADMIN (BackgroundJobResource.OWNER_ADMIN_RESULT_TASK_TYPES). For an
  // editor that is a green row with nothing to click — say why. `completedAt`
  // arrives with the same lazy fetch as `result`, so its presence means "the
  // fetch landed and there was no link", not "still loading".
  const isResultRestricted =
    isCompleted &&
    OWNER_ADMIN_RESULT_TASK_TYPES.has(job.taskType) &&
    !job.result &&
    typeof job.completedAt === "number";
  // Completed batch check-in/out: parse the worker's result JSON so the row
  // explains WHAT happened (processed count, capacity block) instead of a
  // bare checkmark — a capacity-blocked batch is a partial success the user
  // must learn about here.
  const checkInResult =
    isCompleted && category === "batchCheckIn"
      ? parseBatchCheckInResult(job.result)
      : null;
  // PB-TG-5: only on a COMPLETED email job — mid-flight the counter is still
  // climbing and a partial number would read as final.
  const degradedCount =
    isCompleted && category === "email"
      ? parseDegradedSendResult(job.result)
      : null;
  // Only on a COMPLETED import: mid-flight the fan-out of later chunks has not
  // happened yet, and a partial count would read as final.
  const announcementsLost =
    isCompleted && category === "import"
      ? parseImportAnnouncementsLost(job.result)
      : null;
  // Same rule: only a COMPLETED import has a final arrival-notice outcome.
  const arrivalNotice =
    isCompleted && category === "import" ? arrivalNoticeOutcomeOf(job) : null;
  const jobError =
    isFailed && job.errorMessage
      ? category === "sms" && isSmsManualDeadFanout(job.errorMessage)
        ? // A dead SMS fan-out (dlq `fan-out failed: …`): the send broke off
          // while it was being handed out — in words, never the internal
          // text, and no "try again" (audit r1 C8).
          { text: t("sms.fanoutFailed"), raw: null }
        : // A FAILED SMS job's "start unknown" sent nothing for certain:
          // "could not start, nothing was sent" — never the dialog's "check
          // Sent SMS before you send again" (audit r2 R2-FE-1).
          describeJobError(tErrors, category === "sms" ? smsManualEndWordsCode(job.errorMessage) : job.errorMessage, {
            canBuyEventLicence,
            taskType: job.taskType,
          })
      : null;
  // M-02: a by-ids bulk edit's own account of what it left undone — only
  // when the backend sent the counts (T2 on); before that, today's row.
  const bulkUpdate =
    isCompleted && job.taskType === "BULK_UPDATE_GUESTS_BY_IDS"
      ? parseBulkUpdateResult(job.result)
      : null;
  const bulkRefusedCount = bulkUpdate
    ? Object.values(bulkUpdate.failedByCode).reduce((a, b) => a + b, 0)
    : 0;
  // The larger of the two, so the line and the partial icon always agree: a
  // result with refused rows next to `notApplied: 0` must still say why
  // (audit r1 A-3).
  const bulkNotUpdated = bulkUpdate
    ? Math.max(bulkUpdate.notApplied ?? 0, bulkRefusedCount)
    : 0;
  const bulkReasons = bulkUpdate
    ? Array.from(
        new Set(
          Object.entries(bulkUpdate.failedByCode)
            .sort((a, b) => b[1] - a[1])
            .map(([code]) =>
              translateBackendError(tErrors, code, t("bulkUpdate.failedDescription")),
            ),
        ),
      ).slice(0, MAX_BULK_UPDATE_REASONS)
    : [];
  const bulkLeftUndone =
    bulkUpdate !== null && (bulkNotUpdated > 0 || bulkUpdate.notInEvent > 0);
  // PB-TL-3: a COMPLETED job can still have lost items. Completion means
  // "every item reached a terminal outcome", NOT "every item succeeded" —
  // the consumer completes a send once processed+failed+cancelled === total.
  // Distinct from `degradedCount` above, which is a SUCCESSFUL send missing
  // an attachment; these recipients got nothing at all.
  const failedCount =
    isCompleted && (job.failedCount ?? 0) > 0 ? job.failedCount! : null;
  // The bulk-edit summary below says the same thing with its reasons; the
  // generic "N items could not be processed" line would only repeat it. So
  // does an SMS send's counter line ("2 failed · 71 not sent", in guests, not
  // "items") — screens r1 D5.
  const showGenericFailedLine = failedCount !== null && bulkUpdate === null && category !== "sms";
  // A finished run with losses is a PARTIAL success. Rendering the plain
  // green check over it is what the row did before, and it actively misled:
  // a campaign that dropped half its recipients was indistinguishable from a
  // clean one.
  // SMS manual send (F-3): a halted send stopped before every guest was tried
  // — finished, but not as asked. Read on any terminal state.
  const isSms = category === "sms";
  const smsHalt = isSms && isFinished && typeof job.smsHaltReason === "string" && job.smsHaltReason !== ""
    ? job.smsHaltReason
    : null;
  const isPartialSuccess = failedCount !== null || bulkLeftUndone || smsHalt !== null;
  // The Worker's three send counters, each only when present and above zero.
  // Only on a COMPLETED send: mid-flight they are still climbing. Empty is the
  // common case today — the Java poll projection strips them until a later
  // wave — and an empty list renders no line at all.
  const sendCounters: string[] =
    isCompleted && category === "email"
      ? (
          [
            ["email.countFailed", job.failedCount],
            ["email.countSkipped", job.skippedCount],
            ["email.countNoEmail", job.noEmailCount],
          ] as const
        )
          .filter(([, n]) => typeof n === "number" && n > 0)
          .map(([key, n]) => t(key, { count: n as number }))
      : isSms && isFinished
        ? (
            [
              ["sms.countFailed", job.failedCount],
              ["sms.countSkipped", job.skippedCount],
              ["sms.countDryrun", job.dryrunCount],
            ] as const
          )
            .filter(([, n]) => typeof n === "number" && n > 0)
            .map(([key, n]) => t(key, { count: n as number }))
        : [];
  const smsHaltText = smsHalt === null
    ? null
    : (() => {
        const key = smsHaltReasonKey(smsHalt);
        return key ? tHalt(key) : tHalt("unknown", { code: smsHalt });
      })();
  // Only the import-row shape of errors[]; a DLQ entry carries a messageId and
  // no row, and has nothing a person could act on here.
  const rejectedRows = (job.errors ?? []).filter((e) => e.row != null);
  // `row` is 0-based over the SUBMITTED rows. A spreadsheet reader counts from
  // 1 and has a header line above the data, so the row they see is +2 — the
  // same offset collectImportErrors uses for its downloadable error list.
  const rowNumberOffset = 2;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
      data-testid="job-row"
      data-job-id={job.jobId}
    >
      <div className="flex items-start gap-2.5 py-2 px-3 rounded-lg hover:surface-tertiary transition-colors group">
        <div className="mt-0.5">
          <StatusIcon status={job.status} partial={isPartialSuccess} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`body-3-medium truncate ${isFailed ? "font-error" : isCancelled ? "font-tertiary" : "font-primary"}`}>
              {t(`${category}.label`)}
              {isCancelled && ` — ${t("cancelled")}`}
              {job.eventName && (
                <button
                  type="button"
                  onClick={handleEventClick}
                  className="body-4-regular font-tertiary hover:font-primary hover:underline transition-colors cursor-pointer ml-1"
                >
                  {job.eventName}
                </button>
              )}
            </span>
            {relativeTime && (
              <span className="body-4-regular font-tertiary shrink-0 whitespace-nowrap">
                • {relativeTime}
              </span>
            )}
          </div>
          {isInProgress && (
            // The bar was a bare div: no role, no value, nothing announced. A
            // screen reader got the job's label and then silence for the whole
            // run — and this row has no other live signal, since the count line
            // replaced the rotating tagline for the counted lanes.
            //
            // `progressbar` + `aria-valuetext` deliberately, NOT `aria-live`:
            // the value changes about once a second, and a live region would
            // read every one of those out. A progressbar is polled by the
            // reader when the user asks for it, which is the right shape for
            // "how far along is this?".
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percentage}
              aria-valuetext={
                showCount
                  ? t(
                      COUNT_PROGRESS_KEY_OVERRIDES[job.taskType] ??
                        `${category}.countProgress`,
                      { done: countProcessed, total: countTotal },
                    )
                  : `${percentage}%`
              }
              aria-label={t(`${category}.label`)}
              className="w-full h-1 surface-quaternary rounded-full overflow-hidden mt-1.5"
            >
              <motion.div
                className="h-full rounded-full surface-dark"
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
          )}
          {showCount && (
            // aria-hidden because this is a VISUAL duplicate: the progressbar
            // above already carries this exact sentence as its aria-valuetext,
            // which is the element assistive tech is meant to read a progress
            // value from. Without this a screen reader announces the line
            // twice in a row.
            <p
              aria-hidden="true"
              data-testid="job-row-count-progress"
              className="body-4-regular font-tertiary mt-1.5 truncate"
            >
              {t(
                COUNT_PROGRESS_KEY_OVERRIDES[job.taskType] ??
                  `${category}.countProgress`,
                { done: countProcessed, total: countTotal },
              )}
            </p>
          )}
          {showTips && currentTip && (
            <p data-testid="job-row-tip" className="body-4-regular font-tertiary mt-1.5 truncate">
              {currentTip}
            </p>
          )}
          {job.unreachable && isInProgress && (
            <p
              className="body-4-regular font-warning mt-1.5"
              data-testid="job-row-unreachable"
            >
              {/* PB-TL-1: an honest statement about the CONNECTION. The job is
                  still running and still polling — this replaces the invented
                  terminal FAILED the client used to write after 5 bad polls,
                  and clears itself on the first good one. */}
              {t("statusUnreachable")}
            </p>
          )}
          {bulkLeftUndone && bulkUpdate && (
            <div
              className="body-4-regular font-error mt-1.5 space-y-0.5"
              data-testid="job-row-bulk-update-summary"
            >
              {bulkNotUpdated > 0 && (
                <p>{t("bulkUpdate.notUpdated", { count: bulkNotUpdated })}</p>
              )}
              {bulkReasons.length > 0 && (
                <ul className="font-tertiary space-y-0.5">
                  {bulkReasons.map((reason) => (
                    <li key={reason} data-testid="job-row-bulk-update-reason">
                      {reason}
                    </li>
                  ))}
                </ul>
              )}
              {bulkUpdate.notInEvent > 0 && (
                <p>{t("bulkUpdate.notInEvent", { count: bulkUpdate.notInEvent })}</p>
              )}
            </div>
          )}
          {showGenericFailedLine && (
            <p
              className="body-4-regular font-error mt-1.5"
              data-testid="job-row-failed-summary"
            >
              {/* PB-TL-3: these recipients/items did NOT go out. Rendered in
                  the error tone, above the degraded line, because it is the
                  harder failure of the two. */}
              {t("completedWithFailures", { count: failedCount })}
            </p>
          )}
          {rejectedRows.length > 0 && (
            <ul
              className="break-words body-4-regular font-tertiary mt-1 space-y-0.5"
              data-testid="job-row-rejected-rows"
            >
              {/* The rows the import refused, by number and reason. The
                  backend has recorded these since the row-error feature
                  landed and no client ever read them: `errors[]` was written
                  to Mongo, and the operator got "N items could not be
                  processed" with no way to learn which ones. A count alone
                  cannot be acted on — a row number and a reason can.

                  Capped server-side at the 50 newest ($slice: -50), so this
                  list is bounded no matter how large the file was; the line
                  below says so when the count exceeds what we hold. */}
              {rejectedRows.map((e: ImportRowError, i: number) => (
                <li key={`${e.row}-${e.field}-${i}`}>
                  {/* A chunk-level failure carries row -1: there is no line in
                      anyone's spreadsheet to point at, and printing "row 1"
                      for it — which a flat offset does — sends the operator to
                      an innocent guest. */}
                  {(e.row ?? -1) >= 0
                    ? `${tImport("errorListRow")} ${(e.row ?? 0) + rowNumberOffset}`
                    : tImport("errorListReason")}
                  {e.field
                    ? ` · ${tFields.has(e.field) ? tFields(e.field) : e.field}`
                    : ""}
                  {/* The cell as typed: for a party key that is the alias
                      or email the operator has to fix — a row number and
                      "key not found" alone sends them hunting. Only for a
                      real row: a chunk-level entry carries an index here,
                      not a cell. No quote glyphs — they are locale-specific
                      and would have to live in 57 bundles. */}
                  {(e.row ?? -1) >= 0 && e.value ? ` · ${e.value}` : ""}
                  {e.reason
                    ? ` — ${translateBackendError(tErrors, e.reason)}`
                    : ""}
                </li>
              ))}
              {failedCount !== null && failedCount > rejectedRows.length && (
                <li className="font-quaternary">
                  {t("moreFailuresNotListed", {
                    count: failedCount - rejectedRows.length,
                  })}
                </li>
              )}
            </ul>
          )}
          {degradedCount !== null && (
            <p
              className="body-4-regular font-tertiary mt-1.5"
              data-testid="job-row-degraded-summary"
            >
              {/* PB-TG-5: the campaign SUCCEEDED — these recipients got mail,
                  just without the attachment the template asked for. Rendered
                  as a warning line under the success row rather than as a
                  failure, because reporting it as failed would be its own
                  misinformation. */}
              {t("email.attachmentsDegraded", { count: degradedCount })}
            </p>
          )}
          {sendCounters.length > 0 && (
            <p
              className="body-4-regular font-tertiary mt-1.5"
              data-testid="job-row-send-counters"
            >
              {/* "N failed · N skipped · N without email" — the outcome of a
                  finished send by bucket. `failed` restates the error line
                  above beside its siblings; the other two had no line at
                  all, so a send that skipped half its audience read as
                  "all delivered". */}
              {sendCounters.join(" · ")}
            </p>
          )}
          {smsHaltText !== null && (
            <p className="body-4-medium font-orange mt-1.5" data-testid="job-row-sms-halted">
              {t("sms.halted", { reason: smsHaltText })}
            </p>
          )}
          {isSms && isFinished && job.scopeKind !== "company" && job.eventId && (
            <div className="mt-1.5 flex flex-col gap-0.5">
              {/* Only where something may have gone out: a refusal before
                  the first guest says "nothing was sent" itself (FD-11). */}
              {isFailed && !SMS_MANUAL_NOTHING_SENT_CODES.includes(job.errorMessage ?? "") && (
                <p className="body-4-regular font-secondary" data-testid="job-row-sms-failed-check">
                  {t("sms.failedCheckSent")}
                </p>
              )}
              <Link
                to={smsSendViewPath(job.eventId, job.jobId)}
                className="body-4-medium font-secondary underline hover:font-primary w-max"
                data-testid="job-row-sms-open-sent"
              >
                {t("sms.openSentSms")}
              </Link>
            </div>
          )}
          {announcementsLost !== null && (
            <p
              className="body-4-regular font-tertiary mt-1.5"
              data-testid="job-row-announcements-lost"
            >
              {/* The import SUCCEEDED — every guest is written. What is gone is
                  the announcement: welcome mails, RSVP automations, webhooks.
                  Rendered beside the success line rather than as a failure,
                  because calling the import failed would send someone hunting
                  for guests that are already there. Until this line existed the
                  row said "All N entries were imported successfully" and
                  nothing else, which was simply untrue. */}
              {t("import.announcementsLost", { count: announcementsLost })}
            </p>
          )}
          {arrivalNotice !== null && (
            <div className="body-4-regular font-tertiary mt-1.5" data-testid="job-row-arrival-notice">
              <p data-testid="job-row-arrival-notice-summary">
                {t("import.arrivalNoticeSummary", {
                  linked: arrivalNotice.linked,
                  skipped: arrivalNotice.skipped,
                })}
              </p>
              {arrivalNotice.warnings.length > 0 && (
                <ul className="mt-1 flex flex-col gap-0.5" data-testid="job-row-arrival-notice-warnings">
                  {arrivalNotice.warnings.slice(0, ARRIVAL_NOTICE_WARNINGS_SHOWN).map((w, i) => (
                    <li key={`${w.row}-${w.contactId}-${i}`}>
                      {/* Same row numbering as the rejected rows above (0-based
                          submitted row + header line); never an address. */}
                      {typeof w.row === "number" && w.row >= 0
                        ? `${tImport("errorListRow")} ${w.row + rowNumberOffset} — `
                        : ""}
                      {w.reason && ARRIVAL_NOTICE_REASONS.has(w.reason)
                        ? t(`import.arrivalNoticeReason.${w.reason}`)
                        : t("import.arrivalNoticeReasonOther")}
                    </li>
                  ))}
                  {arrivalNotice.warnings.length > ARRIVAL_NOTICE_WARNINGS_SHOWN && (
                    <li className="font-quaternary">
                      {t("moreFailuresNotListed", {
                        count: arrivalNotice.warnings.length - ARRIVAL_NOTICE_WARNINGS_SHOWN,
                      })}
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
          {checkInResult && (
            <p
              className="body-4-regular font-tertiary mt-1.5"
              data-testid="job-row-checkin-summary"
            >
              {/* Reset is checked FIRST and on its own flag: it is not a
                  direction, and the fallback below treats every result that is
                  not literally `checkIn: false` as a check-in.
                  checkIn !== false: old-shape results missing the flag still
                  render as check-in (the common direction). */}
              {t(
                checkInResult.reset === true
                  ? "batchCheckIn.summaryReset"
                  : checkInResult.checkIn !== false
                    ? "batchCheckIn.summaryCheckedIn"
                    : "batchCheckIn.summaryCheckedOut",
                { processed: checkInResult.processed },
              )}
              {checkInResult.blocked === true &&
                typeof checkInResult.currentCount === "number" &&
                typeof checkInResult.capacity === "number" && (
                  <span className="font-primary">
                    {" "}
                    {t("batchCheckIn.summaryBlocked", {
                      currentCount: checkInResult.currentCount,
                      capacity: checkInResult.capacity,
                    })}
                  </span>
                )}
              {(checkInResult.warningsCount ?? 0) > 0 && (
                <span>
                  {" "}
                  {t("batchCheckIn.summaryWarnings", {
                    count: checkInResult.warningsCount ?? 0,
                  })}
                </span>
              )}
            </p>
          )}
          {/* Portal-link export that finished WITHOUT links: the file exists,
              the column is empty, and only this line says why (plugin off,
              row cap, kill switch, signing failure). A green check alone
              would read as "links included". */}
          {isCompleted && job.portalLinks?.skipped && (
            <p
              className="body-4-regular font-orange mt-0.5"
              data-testid="background-jobs-portal-links-skipped"
              data-reason={job.portalLinks.skipped}
            >
              {t.has(`portalLinksSkipped.${job.portalLinks.skipped}`)
                ? t(`portalLinksSkipped.${job.portalLinks.skipped}`)
                : t.has("portalLinksSkipped.SIGN_FAILED")
                  ? t("portalLinksSkipped.SIGN_FAILED")
                  : job.portalLinks.skipped}
            </p>
          )}
          {isFailed && jobError && (
            <>
              {/* Backend failures arrive as error codes (e.g. "eventAtCapacity")
                  or, from older producers, as English ("invalidDomain: …").
                  Both read as a sentence; an unknown text as the generic
                  "the task failed" line (M-29). A quota refusal offers an
                  event licence only where one can be bought. */}
              {jobError.raw !== null ? (
                // The producer's own text sits one press away — support needs
                // it, the row must not lead with it. A real button (keyboard,
                // announced as expandable); the line itself is its name, so
                // no new copy is needed (audit r1 C-3).
                <button
                  type="button"
                  data-testid="job-row-error"
                  className="flex w-full min-w-0 items-start gap-1 text-left body-4-regular font-error mt-0.5 cursor-pointer"
                  onClick={() => setErrorExpanded((prev) => !prev)}
                  aria-expanded={errorExpanded}
                  aria-controls={errorDetailId}
                >
                  <span className={`min-w-0 ${errorExpanded ? "break-words" : "truncate"}`}>
                    {jobError.text}
                  </span>
                  {errorExpanded ? (
                    <IconChevronUp size={12} className="shrink-0 mt-0.5" aria-hidden="true" />
                  ) : (
                    <IconChevronDown size={12} className="shrink-0 mt-0.5" aria-hidden="true" />
                  )}
                </button>
              ) : (
                <p
                  data-testid="job-row-error"
                  className={`body-4-regular font-error mt-0.5 cursor-pointer ${errorExpanded ? "" : "truncate"}`}
                  onClick={() => setErrorExpanded((prev) => !prev)}
                >
                  {jobError.text}
                </p>
              )}
              {errorExpanded && jobError.raw !== null && (
                <p
                  id={errorDetailId}
                  data-testid="job-row-error-detail"
                  className="body-4-regular font-tertiary mt-0.5 break-words"
                >
                  {jobError.raw}
                </p>
              )}
            </>
          )}
          {isDownloadExpired && job.result && (
            <p
              className="body-4-regular font-tertiary mt-1.5"
              data-testid="job-row-download-expired"
            >
              {t("downloadExpired")}
            </p>
          )}
          {isResultRestricted && (
            <p className="body-4-regular font-tertiary mt-1.5" data-testid="job-row-result-restricted">
              {t("resultRestricted")}
            </p>
          )}
          {resultFiles && (
            <ul className="mt-2 flex flex-col gap-1.5" data-testid="job-row-downloads">
              {resultFiles.map((file, idx) => (
                <li key={file.name} className="flex flex-row flex-wrap items-center gap-x-2 gap-y-0.5">
                  <a
                    href={file.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`job-row-download-part-${idx + 1}`}
                    className="inline-flex items-center gap-1.5 body-3-medium font-primary px-2 py-0.75 rounded-lg border outline-quaternary hover:surface-tertiary transition-colors"
                  >
                    <IconDownload size={14} aria-hidden="true" />
                    {t("downloadPart", { n: idx + 1, total: resultFiles.length })}
                  </a>
                  <span className="body-4-regular font-tertiary">
                    {t("partContents", { files: file.files, size: formatFileSize(file.bytes) })}
                  </span>
                  {file.missing > 0 && (
                    <span className="body-4-regular font-warning" data-testid={`job-row-part-missing-${idx + 1}`}>
                      {t("partMissing", { count: file.missing })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {hasDownload && !resultFiles && (
            <a
              href={job.result}
              download
              // Open in a new tab: presigned export URLs are cross-origin, so
              // browsers ignore the `download` hint and would otherwise
              // navigate the current tab away from the app (killing the popup).
              // A new tab lets the browser handle the file (PDF/CSV) safely.
              target="_blank"
              rel="noopener noreferrer"
              data-testid="job-row-download"
              className="inline-flex items-center gap-1.5 body-3-medium font-primary mt-2 px-2 py-0.75 rounded-lg border outline-quaternary hover:surface-tertiary transition-colors"
            >
              <IconDownload size={14} />
              {t("download")}
            </a>
          )}
        </div>

        {isInProgress && (
          <span className="body-4-bold font-secondary shrink-0 w-8 text-right mt-0.5">
            {percentage}%
          </span>
        )}

        {/* PB-TL-6: both controls call a mutating endpoint that now requires
            >= EDITOR, so a read-only collaborator gets neither. The row itself
            still renders — the job list stays readable. */}
        {/* A finished row of a deleted event may always be dismissed. Without
            a mutating role that dismiss stays local (no DELETE the server
            would refuse) — there is no event left to protect (R3-1, R4-1). */}
        {(isInProgress ? canMutate : canMutate || eventGone) &&
          (isInProgress ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCancelConfirm(true);
              }}
              data-testid="job-row-cancel"
              className="shrink-0 mt-0.5 transition-opacity"
            >
              <IconX
                size={12}
                className="font-tertiary hover:font-error transition-colors"
              />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(canMutate ? undefined : { local: true });
              }}
              data-testid="job-row-dismiss"
              className="shrink-0 mt-0.5 transition-opacity"
            >
              <IconX
                size={12}
                className="font-tertiary hover:font-error transition-colors"
              />
            </button>
          ))}
      </div>
      <JobCancelConfirm
        open={showCancelConfirm}
        isSms={isSms}
        onCancel={onCancel}
        onClose={() => setShowCancelConfirm(false)}
      />
    </motion.div>
  );
};

/**
 * "Cancel task" — the confirm behind the tray's ✕ on a running row, and behind
 * the per-send view's own cancel of a send that never started (audit r3 R3-1):
 * one confirm, one outcome rule.
 */
export const JobCancelConfirm: React.FC<{
  open: boolean;
  /** A manual SMS send: its own message (sent SMS stay billed). */
  isSms: boolean;
  onCancel: () => Promise<CancelJobOutcome>;
  onClose: () => void;
}> = ({ open, isSms, onCancel, onClose }) => {
  const t = useTranslations("backgroundJobs");
  const tErrors = useTranslations("errors");
  const cancelAction = useActionError();
  const { showToast } = useToast();
  return (
    // Local Suspense so first-load chunk doesn't bubble up to App.tsx covering spinner.
    <AnimatePresence>
      {open && (
        <Suspense fallback={null}>
          <PDeleteConfirmation
            title={t("cancelTitle")}
            message={isSms ? t("sms.cancelMessage") : t("cancelMessage")}
            primaryButtonText={t("cancelConfirm")}
            isLoading={cancelAction.busy}
            error={cancelAction.error ?? undefined}
            onCloseAction={() => {
              cancelAction.clear();
              onClose();
            }}
            onConfirmAction={async () => {
              // M-12: close only on an outcome. A refusal stays in the
              // dialog with its reason; a job that had already ended
              // closes with that note instead of a silent "cancelled".
              const r = await cancelAction.run(onCancel, { fallback: tErrors("unknown") });
              if (!r.ok) return;
              onClose();
              if (r.value === "alreadyFinished") {
                showToast(tErrors("jobAlreadyFinished"), "info");
              }
            }}
          />
        </Suspense>
      )}
    </AnimatePresence>
  );
};

/**
 * Flat, unscoped list of all tracked jobs — rendered inside the notifications
 * bell popup's "Aufgaben" tab. Unlike the floating {@link PBackgroundJobs}
 * panel (which filters to the current event/company scope so it can't cover
 * an unrelated event's UI), the bell is a global header surface, so it shows
 * every job the user has in flight or recently finished regardless of route.
 * This is the durable "reopen" home once the floating panel is dismissed.
 */
export const BackgroundJobsList: React.FC = () => {
  const { jobs, dismissJob, cancelJob } = useBackgroundJobs();
  const t = useTranslations("backgroundJobs");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (jobs.length === 0) return;
    const interval = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(interval);
  }, [jobs.length]);

  if (jobs.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center flex-1 py-10"
        data-testid="background-jobs-empty"
      >
        <p className="body-2-bold font-secondary p-4">{t("noJobs")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="background-jobs-list">
      <AnimatePresence mode="popLayout">
        {jobs.map((job) => (
          <JobRow
            key={job.jobId}
            job={job}
            onDismiss={(opts) => dismissJob(job.jobId, opts)}
            onCancel={() => cancelJob(job.jobId)}
            tick={tick}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Persisted set of job ids the user has dismissed from the floating panel via
// its header ✕. Kept in localStorage (separate from the jobs store itself) so
// a dismissal survives a page reload — otherwise the panel would re-appear on
// refresh while the jobs are still tracked. A brand-new job (id not in this
// set) re-surfaces the panel; the jobs always stay reachable in the bell.
const HIDDEN_STORAGE_KEY = "veltric-background-jobs-hidden";

function loadHiddenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveHiddenIds(ids: Set<string>) {
  try {
    localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* quota / disabled storage — dismissal just won't persist */
  }
}

export const PBackgroundJobs: React.FC = () => {
  const { jobs, dismissJob, cancelJob } = useBackgroundJobs();
  const t = useTranslations("backgroundJobs");
  const pathname = useLocation().pathname;
  const { state: authState } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(true);
  // The floating panel is dismissable via its header ✕. Dismissing only hides
  // this overlay — the jobs live on and stay reachable in the notifications
  // bell's "Aufgaben" tab. The dismissed job ids persist in localStorage so the
  // panel stays closed across reloads; a NEW job (id not yet hidden) re-surfaces
  // it for live feedback.
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => loadHiddenIds());
  // This component is mounted at the ROOT (main.tsx), so it survives a logout
  // and kept the previous account's dismissed ids in state — the next
  // dismissal then wrote them straight back into the key the teardown had just
  // swept. Twin of the same listener in BackgroundJobsProvider.
  useEffect(() => {
    const onAccountTeardown = () => setHiddenIds(new Set());
    window.addEventListener(ACCOUNT_TEARDOWN_EVENT, onAccountTeardown);
    return () =>
      window.removeEventListener(ACCOUNT_TEARDOWN_EVENT, onAccountTeardown);
  }, []);
  const [tick, setTick] = useState(0);
  // Hide while a blocking full-screen / docked overlay is open (slotting editors,
  // export/send/check-in modals, …) so this bottom-right z-300 panel can't cover
  // the overlay's action buttons — same signal the onboarding floaters use.
  const overlayOpen = useCoachStore((s) => s.overlayCount > 0);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const isAuthRoute = pathname?.startsWith("/auth");
  // The shell-less create-event wizard owns its bottom-right corner (Velo dock
  // z-90, plan-checkout pay button z-100 — both under this panel's z-300).
  // Company-scope jobs would otherwise render here; they stay reachable in the
  // notifications bell.
  const isImmersive = isCreateEventRoute(pathname);

  // Extract eventId from URL to filter jobs by current event. Two URL shapes
  // carry an eventId: the standard event routes (`/event/<eventId>/…`) and the
  // builder routes (`/<kind>-builder/<eventId>/…` for email/document/website/
  // wallet/calendar). The builders live OUTSIDE the event shell, so without the
  // builder branch a job submitted from a builder (e.g. the AI translate job)
  // would be filtered out and the panel would never render there.
  const currentEventId = useMemo(() => {
    const eventMatch = pathname?.match(/\/event\/([^/]+)/);
    if (eventMatch?.[1]) return eventMatch[1];
    // Builder routes also expose `/<kind>-builder/template/<templateId>/…`
    // (template-only mode, no event) — exclude that by skipping "template".
    const builderMatch = pathname?.match(
      /\/(?:email|document|website|wallet|calendar)-builder\/([^/]+)/,
    );
    if (builderMatch?.[1] && builderMatch[1] !== "template") {
      return builderMatch[1];
    }
    return null;
  }, [pathname]);

  // Company-scope jobs (EXPORT_EVENTS_* via submitBackgroundJobCompany) store
  // companyId in the eventId slot — they belong to the user, not any one event.
  // Surface them on dashboard routes so the user can see their company-wide
  // exports in flight regardless of which event they're inside.
  const companyId =
    authState.kind === "authenticated" ? authState.companyId : null;

  const filteredJobs = useMemo(() => {
    if (currentEventId) {
      return jobs.filter(
        (j) => j.eventId === currentEventId || j.eventId === companyId,
      );
    }
    if (companyId) {
      return jobs.filter((j) => j.eventId === companyId);
    }
    return [];
  }, [jobs, currentEventId, companyId]);

  useEffect(() => {
    if (filteredJobs.length === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, [filteredJobs.length]);

  // Prune hidden ids whose job no longer exists (dismissed/expired) so the set
  // can't grow unbounded and a future job can't accidentally reuse a stale id.
  // Skip while `jobs` is empty: the provider hydrates from localStorage async,
  // so an empty list here usually means "not loaded yet" — pruning against it
  // would wipe a just-persisted dismissal and the panel would reappear on
  // reload. A genuinely-empty store keeps a few stale ids, which is harmless.
  useEffect(() => {
    if (jobs.length === 0) return;
    const allIds = new Set(jobs.map((j) => j.jobId));
    let changed = false;
    const next = new Set<string>();
    for (const id of hiddenIds) {
      if (allIds.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) {
      setHiddenIds(next);
      saveHiddenIds(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  // Only the jobs the user hasn't dismissed drive visibility. A genuinely new
  // job (id not in the hidden set) re-surfaces the panel for live feedback;
  // same-id progress ticks and a reload both keep a dismissal in effect.
  const visibleJobs = filteredJobs.filter((j) => !hiddenIds.has(j.jobId));

  const dismissPanel = () => {
    const next = new Set(hiddenIds);
    for (const j of filteredJobs) next.add(j.jobId);
    setHiddenIds(next);
    saveHiddenIds(next);
  };

  if (
    !mounted ||
    visibleJobs.length === 0 ||
    isAuthRoute ||
    isImmersive ||
    overlayOpen
  )
    return null;

  const activeCount = visibleJobs.filter((j) => j.status === "IN_PROGRESS").length;
  const completedCount = visibleJobs.filter((j) => j.status === "COMPLETED").length;
  const failedCount = visibleJobs.filter((j) => j.status === "FAILED").length;

  return createPortal(
    // sm:right-[calc(…+var(--ai-rail-push))]: the docked AI drawer (340px,
    // z-10000) only pushes the layout shells — fixed floaters must shift
    // themselves or they vanish under the rail. Transition mirrors the shells'
    // 250ms padding tween so panel and content move as one. Mobile keeps plain
    // right-4: there the drawer is a full-screen overlay, not a rail.
    <motion.div
      className="fixed bottom-4 right-4 sm:right-[calc(1rem+var(--ai-rail-push))] transition-[right] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none z-300"
      data-testid="background-jobs-panel"
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ type: "spring", damping: 25, stiffness: 350 }}
    >
      {/* Width: ~384px on desktop, capped at 92vw on small screens so the
          panel never sticks past the viewport edge. The rotating tip lines
          (~40-50 chars in EN/DE) need more than the prior `w-72` (288px)
          to render without truncation. */}
      <div className="surface-card border outline-secondary rounded-xl shadow-lg w-[min(92vw,24rem)] overflow-hidden">
        {/* Header — expand toggle (flex-1) + a ✕ that dismisses the whole
            floating panel. Split into two sibling buttons (no nested buttons)
            so the ✕ can stopPropagation independently of the collapse toggle. */}
        <div className="w-full flex items-center px-3.5 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            data-testid="background-jobs-panel-toggle"
            className="flex flex-1 min-w-0 items-center justify-between gap-2 hover:opacity-70 transition-opacity"
          >
            <div className="flex items-center gap-2 min-w-0">
              {activeCount > 0 ? (
                <IconLoader2 size={14} className="font-blue animate-spin" />
              ) : failedCount > 0 ? (
                <IconX size={14} className="font-error" />
              ) : (
                <IconCheck size={14} className="font-success" />
              )}
              <span
                className="body-3-medium font-primary truncate"
                data-testid="background-jobs-panel-status"
              >
                {activeCount > 0
                  ? t("activeJobs", { count: activeCount })
                  : completedCount > 0 && failedCount === 0
                    ? t("allCompleted")
                    : t("jobsDone")}
              </span>
            </div>
            {expanded ? (
              <IconChevronDown size={14} className="font-tertiary shrink-0" />
            ) : (
              <IconChevronUp size={14} className="font-tertiary shrink-0" />
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismissPanel();
            }}
            aria-label={t("close")}
            data-testid="background-jobs-panel-dismiss"
            className="ml-2 shrink-0"
          >
            <IconX
              size={14}
              className="font-tertiary hover:font-error transition-colors"
            />
          </button>
        </div>

        {/* Job list */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div
                className="border-t outline-secondary px-1 py-1 max-h-52 overflow-y-auto custom-scrollbar"
                data-testid="background-jobs-panel-list"
              >
                <AnimatePresence mode="popLayout">
                  {visibleJobs.map((job) => (
                    <JobRow
                      key={job.jobId}
                      job={job}
                      onDismiss={(opts) => dismissJob(job.jobId, opts)}
                      onCancel={() => cancelJob(job.jobId)}
                      tick={tick}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>,
    document.body
  );
};
